import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../core/services/api.service';
import { Agency, AgencyPayload } from '../../core/models/api.models';

@Component({
  selector: 'app-agencies',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule],
  templateUrl: './agencies.component.html',
  styleUrl: './agencies.component.scss',
})
export class AgenciesComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly fb = inject(FormBuilder);

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);
  readonly rows = signal<Agency[]>([]);
  readonly page = signal(1);
  readonly total = signal(0);
  readonly limit = 20;
  readonly showForm = signal(false);
  readonly editingId = signal<number | null>(null);

  search = '';

  pendingImage: File | null = null;
  pendingPreview: string | null = null;

  readonly form = this.fb.nonNullable.group({
    name: ['', Validators.required],
    slug: [''],
    subtitle: [''],
    image_url: [''],
    account_number: [''],
    bsb: [''],
    bank_name: [''],
    pay_id: [''],
    pay_id_type: [''],
    pay_id_name: [''],
    currency: ['AUD'],
    is_active: [true],
    show_on_mobile: [true],
    sort_order: [0],
  });

  ngOnInit(): void {
    this.load();
  }

  load(page = 1): void {
    this.loading.set(true);
    this.error.set(null);
    this.page.set(page);
    this.api.getAgencies(page, this.limit, this.search).subscribe({
      next: (data) => {
        this.rows.set(data?.rows || []);
        this.total.set(data?.total || 0);
        this.loading.set(false);
      },
      error: (err: { message?: string }) => {
        this.error.set(err?.message || 'Failed to load agencies');
        this.loading.set(false);
      },
    });
  }

  searchNow(): void {
    this.load(1);
  }

  private clearPendingImage(): void {
    if (this.pendingPreview) URL.revokeObjectURL(this.pendingPreview);
    this.pendingImage = null;
    this.pendingPreview = null;
  }

  openCreate(): void {
    this.editingId.set(null);
    this.clearPendingImage();
    this.form.reset({
      name: '',
      slug: '',
      subtitle: 'Real estate agency',
      image_url: '',
      account_number: '',
      bsb: '',
      bank_name: '',
      pay_id: '',
      pay_id_type: '',
      pay_id_name: '',
      currency: 'AUD',
      is_active: true,
      show_on_mobile: true,
      sort_order: 0,
    });
    this.showForm.set(true);
    this.success.set(null);
    this.error.set(null);
  }

  openEdit(row: Agency): void {
    this.editingId.set(row.id);
    this.clearPendingImage();
    this.form.patchValue({
      name: row.name || '',
      slug: row.slug || '',
      subtitle: row.subtitle || '',
      image_url: row.image_url || '',
      account_number: row.account_number || '',
      bsb: row.bsb || row.bank_code || '',
      bank_name: row.bank_name || '',
      pay_id: row.pay_id || '',
      pay_id_type: row.pay_id_type || '',
      pay_id_name: row.pay_id_name || '',
      currency: row.currency || 'AUD',
      is_active: row.is_active !== false,
      show_on_mobile: row.show_on_mobile !== false,
      sort_order: Number(row.sort_order || 0),
    });
    this.showForm.set(true);
    this.success.set(null);
    this.error.set(null);
  }

  cancelForm(): void {
    this.showForm.set(false);
    this.editingId.set(null);
    this.clearPendingImage();
  }

  private buildPayload(): AgencyPayload | null {
    const v = this.form.getRawValue();
    const name = v.name.trim();
    if (!name) {
      this.error.set('Name is required');
      return null;
    }

    const account = v.account_number.trim();
    const bsb = v.bsb.trim();
    const payId = v.pay_id.trim();
    const payIdType = v.pay_id_type.trim();

    if (!this.editingId()) {
      const hasBank = !!(account && bsb);
      const hasPayId = !!(payId && payIdType);
      if (!hasBank && !hasPayId) {
        this.error.set('Provide bank (account + BSB) or PayID (pay_id + type) for payout');
        return null;
      }
    }

    return {
      name,
      slug: v.slug.trim() || undefined,
      subtitle: v.subtitle.trim() || null,
      image_url: v.image_url.trim() || null,
      account_number: account || null,
      bsb: bsb || null,
      bank_code: bsb || null,
      bank_name: v.bank_name.trim() || null,
      pay_id: payId || null,
      pay_id_type: payIdType || null,
      pay_id_name: v.pay_id_name.trim() || null,
      currency: v.currency.trim() || 'AUD',
      is_active: !!v.is_active,
      show_on_mobile: !!v.show_on_mobile,
      sort_order: Number(v.sort_order || 0),
    };
  }

  save(): void {
    if (this.form.invalid || this.saving()) {
      this.form.markAllAsTouched();
      return;
    }
    const payload = this.buildPayload();
    if (!payload) return;

    this.saving.set(true);
    this.error.set(null);
    const id = this.editingId();
    const req$ = this.pendingImage
      ? this.api.saveAgencyMultipart(payload, this.pendingImage, id)
      : id
        ? this.api.updateAgency(id, payload)
        : this.api.createAgency(payload);

    req$.subscribe({
      next: () => {
        this.saving.set(false);
        this.showForm.set(false);
        this.editingId.set(null);
        this.clearPendingImage();
        this.success.set(id ? 'Agency updated' : 'Agency created');
        this.load(this.page());
      },
      error: (err: { message?: string }) => {
        this.saving.set(false);
        this.error.set(err?.message || 'Failed to save agency');
      },
    });
  }

  remove(row: Agency): void {
    if (!confirm(`Delete agency “${row.name}”?`)) return;
    this.saving.set(true);
    this.api.deleteAgency(row.id).subscribe({
      next: () => {
        this.saving.set(false);
        this.success.set('Agency deleted');
        this.load(this.page());
      },
      error: (err: { message?: string }) => {
        this.saving.set(false);
        this.error.set(err?.message || 'Failed to delete agency');
      },
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (this.pendingPreview) URL.revokeObjectURL(this.pendingPreview);
    this.pendingImage = file;
    this.pendingPreview = URL.createObjectURL(file);
    this.success.set('Image selected — will upload to S3 when you save');
  }

  previewUrl(): string | null {
    return this.pendingPreview || this.form.get('image_url')?.value || null;
  }

  bankLabel(row: Agency): string {
    const parts = [row.bsb || row.bank_code, row.account_number, row.bank_name].filter(Boolean);
    if (parts.length) return parts.join(' · ');
    if (row.pay_id) return `PayID: ${row.pay_id}`;
    return '—';
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.total() / this.limit));
  }
}
